import React from 'react';
import QRCode from 'react-qr-code';
import { WarningCircle, Package } from '@phosphor-icons/react';

import { MarketShell } from '../../design-system/components/shells';
import { Button, Empty, PageHeader, SectionTitle } from '../../design-system/components/desktop';
import { DetailList, DetailRow } from '../../design-system/patterns/console';
import { AuthBanner } from '../../design-system/patterns/auth';
import { ModalField, ModalInput } from '../../design-system/patterns/modal-form';
import { routes } from '../../routes/routes';

import './create-label.css';

/**
 * /create-label, the view. Pure.
 *
 * THE COURIER BANNER IS GONE. The retired page printed
 * `labelData.provider.name` in 3rem, 800-weight letters as the headline of the
 * whole screen. Sleevr does not name a courier anywhere in its interface, and
 * a page whose loudest element is a carrier's brand is the clearest possible
 * breach of that. What replaces it is the thing the seller actually came for:
 * the tracking reference, in tabular figures, next to the control that
 * downloads the real waybill.
 *
 * NO PRICES. The shipment payload carries none and none is invented. There is
 * no shipping cost, no quote and no "R" anywhere on this page.
 *
 * The From and To blocks stay. They are the parcel's own addressing, taken
 * straight from the shipment this seller just created for their own order, and
 * without them the seller cannot check the label is right before printing it.
 * They are not a marketplace claim about where stock ships from, which is what
 * the origin rule is about. Flagged in the handover for a human call.
 */

function AddressBlock({ title, address }) {
  if (!address) return null;
  const locality = [address.city, address.zone_name].filter(Boolean).join(', ');
  const postal = [address.code, address.country].filter(Boolean).join(', ');

  return (
    <div>
      <SectionTitle as="h3">{title}</SectionTitle>
      <DetailList>
        <DetailRow label="Name" value={address.company} />
        <DetailRow label="Street" value={address.street_address} />
        <DetailRow label="Area" value={locality} />
        <DetailRow label="Postal" value={postal} />
      </DetailList>
    </div>
  );
}

export function CreateLabelView({
  orderId = '',
  trackingId = '',
  labelData = null,
  isPending = false,
  error,
  onGenerate,
  onDownload,
  downloading = false,
}) {
  return (
    <MarketShell
      title="Create label"
      breadcrumbs={[
        { label: 'Selling' },
        { label: 'Orders', to: routes.DASHBOARD },
        { label: 'Create label' },
      ]}
    >
      <PageHeader
        title="Create label"
        sub="Generate the shipping label for this order, then download the waybill and attach it to the parcel."
      />

      <div className="dsa-lbl-stack">
        <section className="dsa-lbl-panel">
          <AuthBanner tone="error">{error}</AuthBanner>

          {orderId ? (
            <>
              <ModalField label="Order ID">
                <ModalInput value={orderId} readOnly locked />
              </ModalField>

              <div className="dsa-lbl-actions">
                <Button
                  variant="primary"
                  onClick={onGenerate}
                  disabled={isPending || Boolean(labelData)}
                >
                  {isPending ? 'Generating...' : labelData ? 'Label created' : 'Generate label'}
                </Button>
              </div>
            </>
          ) : (
            /* Reached directly rather than from an order row, so there is no
               order to label. Said plainly, with the way back. */
            <Empty
              icon={WarningCircle}
              illustration="not-found"
              /* Nothing failed: the page was opened without an order reference,
                 so the address it arrived with points at nothing. `error` was
                 the wrong tone for a dead end. */
              title="No order to label"
              text="Open this page from the order you want to ship, so the order reference comes with you."
              actions={
                <Button as="a" variant="ghost" href={routes.DASHBOARD}>
                  Back to orders
                </Button>
              }
            />
          )}
        </section>

        {labelData ? (
          <section className="dsa-lbl-panel">
            <div className="dsa-lbl-head">
              <div>
                <SectionTitle as="h2">Tracking reference</SectionTitle>
                <p className="dsa-lbl-track">{trackingId ? `#${trackingId}` : 'Not issued yet'}</p>
              </div>
              {trackingId ? (
                <Button variant="ghost" onClick={onDownload} disabled={downloading}>
                  {downloading ? 'Fetching waybill...' : 'Download waybill'}
                </Button>
              ) : null}
            </div>

            <div className="dsa-lbl-grid">
              <div className="dsa-lbl-qr">
                {trackingId ? (
                  <QRCode value={String(trackingId)} size={160} />
                ) : (
                  <Empty
                    icon={Package}
                    size="sm"
                    inline
                    title="No code yet"
                    text="The code appears once the tracking reference is issued."
                  />
                )}
              </div>

              <AddressBlock title="Collected from" address={labelData.collection_address} />
              <AddressBlock title="Delivering to" address={labelData.delivery_address} />
            </div>
          </section>
        ) : null}
      </div>
    </MarketShell>
  );
}

export default CreateLabelView;
